import { forwardRef, useRef, useState } from 'react';
import type { PropsWithoutRef, MouseEvent, ChangeEvent, FocusEvent } from 'react';

import { useConsolidatedRef, useI18n, useUID } from '../../hooks';
import type { Action, NoChildrenProp, RefElement, WithAttributes } from '../../types';
import Button from '../Button';
import type { ButtonProps } from '../Button';
import Icon from '../Icon';
import MenuButton from '../MenuButton';
import type { MenuButtonProps } from '../MenuButton';
import type { MenuProps } from '../Menu';
import { createStringMatcher, isInstance } from '../../utils';

export type ActionsProps = WithAttributes<
  'button',
  NoChildrenProp &
    Pick<ButtonProps, 'compact' | 'disabled'> &
    Pick<MenuProps, 'scrollAt'> & {
      items: Action[];
      contextualLabel?: string;
      /** @default 2 */
      menuAt?: number;
      filterAt?: number;
      progress?: boolean;
      menuButtonProps?: {
        onClick?: MenuButtonProps['onClick'];
        onKeyDown?: MenuButtonProps['onKeyDown'];
      };
      /** @default true */
      iconOnly?: boolean;
      /** @default false */
      compact?: ButtonProps['compact'];
    }
>;

export default forwardRef<RefElement<ActionsProps>, PropsWithoutRef<ActionsProps>>(function Actions(
  {
    // @ts-expect-error
    'data-testid': dataTestId,
    items,
    menuAt = 2,
    filterAt,
    scrollAt,
    progress,
    iconOnly = true,
    menuButtonProps,
    contextualLabel,
    compact = false,
    disabled = false,
    tabIndex
  },
  ref
) {
  const t = useI18n();
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useConsolidatedRef(ref);
  const filterElRef = useConsolidatedRef<HTMLInputElement>(null);
  const filterListId = useUID();
  const [filterValue, setFilterValue] = useState('');

  if (!items) {
    return null;
  }

  const renderFilter = typeof filterAt === 'number' && items.length >= filterAt;
  const filterRegex = createStringMatcher(filterValue, 'contains');
  const itemsToRender = renderFilter ? items.filter(item => filterRegex.test(item.text)) : items;

  const onBlur = (e: FocusEvent) => {
    if (isInstance(e.relatedTarget, Node) && menuRef.current?.contains(e.relatedTarget)) {
      return;
    }
    setFilterValue('');
  };

  const filterInputProps = renderFilter
    ? {
        value: filterValue,
        ref: filterElRef,
        onChange: (e: ChangeEvent<HTMLInputElement>) => setFilterValue?.(e.target.value),
        onBlur
      }
    : undefined;

  return items.length >= menuAt ? (
    <MenuButton
      {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      ref={menuButtonRef}
      text={t('actions')}
      aria-label={contextualLabel && t('actions_for', [contextualLabel])}
      iconOnly
      icon='more'
      variant='simple'
      compact={compact}
      onClick={menuButtonProps?.onClick}
      onKeyDown={menuButtonProps?.onKeyDown}
      disabled={disabled}
      tabIndex={tabIndex}
      menu={{
        scrollAt,
        filterInputProps: progress ? undefined : filterInputProps,
        listId: filterListId,
        items: itemsToRender.map(({ text, onClick, ...restProps }) => ({
          ...restProps,
          primary: text,
          onClick: onClick
            ? (id, e) => {
                onClick(id, e, menuButtonRef.current ?? undefined);
                menuButtonRef.current?.focus();
              }
            : undefined
        })),
        loading: progress
      }}
    />
  ) : (
    <>
      {items.map(({ id, icon, text, onClick, ...restProps }, index) => {
        return (
          <Button
            {...(dataTestId ? { 'data-testid': dataTestId } : {})}
            ref={index === 0 ? menuButtonRef : undefined}
            key={id}
            variant={icon && iconOnly ? 'simple' : undefined}
            onClick={(event: MouseEvent<HTMLButtonElement>) => onClick?.(id, event)}
            label={icon ? text : undefined}
            icon={!!icon && iconOnly}
            compact={compact}
            aria-label={contextualLabel ? `${text} - ${contextualLabel}` : text}
            disabled={disabled}
            {...restProps}
          >
            {icon && iconOnly ? <Icon name={icon} /> : text}
          </Button>
        );
      })}
    </>
  );
});
